import useSWR from "swr";
import { useSDK } from "@contentful/react-apps-toolkit";
import { FetchProps, Glossary, GlossaryId, swrKeys } from "../lib";
import { getPlanFromCredentials } from "../utils";
import { GlossaryResponse } from "./types";

export const glossaryFromGlossaryResponse = (response: GlossaryResponse): Glossary => {
    const glossary: Glossary = {
        id: response.glossary_id,
        name: response.name,
        lastUpdated: response.creation_time,
        entryCount: 0,
        locales: [],
    };

    for (const dictionary of response.dictionaries) {
        glossary.entryCount += dictionary.entry_count;
        glossary.locales.push({
            source: dictionary.source_lang.toUpperCase(),
            target: dictionary.target_lang.toUpperCase(),
        });
    }

    return glossary;
};

const DeeplGlossaryFetcher = async (
    id: GlossaryId | undefined,
    credentials: string
): Promise<Glossary | undefined> => {
    if (!id) {
        return undefined;
    }

    const plan = getPlanFromCredentials(credentials);

    const response = await fetch(`/api/${plan}/v3/glossaries/${id}`, {
        method: "GET",
        headers: {
            Authorization: `DeepL-Auth-Key ${credentials}`,
            Connection: "keep-alive",
        },
    });

    if (!response.ok) {
        switch (response.status) {
            case 404:
                console.error(
                    'Selected glossary couldn\'t be found. This could be because another user deleted the glossary while you had it selected. This does not prevent the app from working, we will treat it as if no glossary had been selected.\n\nTo make this error go away, go to the Config Screen and hit "Save" once.'
                );
                return undefined;
            default:
                throw new Error(`Error fetching DeepL glossary. Status: ${response.status}`);
        }
    }

    const data: GlossaryResponse = await response.json();

    return glossaryFromGlossaryResponse(data);
};

export const useFetchDeeplGlossary = (id?: GlossaryId, props?: FetchProps) => {
    const { skip } = props ?? {};
    const sdk = useSDK();
    const deeplCredentials = sdk.parameters.installation.deeplApiKey;

    const { data, isLoading } = useSWR(
        [swrKeys.deeplGlossary, deeplCredentials, id, !!skip],
        () => (!skip ? DeeplGlossaryFetcher(id, deeplCredentials) : undefined),
        { revalidateOnMount: true }
    );

    return {
        data,
        isLoading,
    };
};
